import { css, html, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn } from '../luna-common';
import '../luna-icon/luna-icon';

const ROLES = ['success', 'info'] as const;
type ToastRole = (typeof ROLES)[number];

// Toast — figma "Toast" (component 245:54): brief confirmation for Save
// checkpoint / Undo / Redo, so the action is never silent. Centred in the
// viewport (as in the 252:1715 mock-up), persists until the user's next
// click/tap/keypress anywhere (no auto-timeout — WCAG 2.2.1), role=status
// aria-live=polite, and NEVER blocks input.
@customElement('luna-toast')
export class LunaToast extends LitPage {
  @property({ type: Boolean }) open = false;
  @property({ attribute: 'message' }) message = '';

  private _role: ToastRole = 'success';

  @property({ attribute: 'role' })
  get role(): ToastRole {
    return this._role;
  }

  set role(value: string | null) {
    const next = String(value ?? '');
    if (!(ROLES as readonly string[]).includes(next)) {
      devWarn(`luna-toast: unknown role "${next}" — falling back to "success"`);
      this._role = 'success';
      return;
    }
    this._role = next as ToastRole;
  }

  private _onGlobalInteraction = () => this.dismiss();

  updated(changed: PropertyValues): void {
    if (changed.has('open')) {
      if (this.open) {
        this.arm();
      } else {
        this.disarm();
      }
    }
  }

  disconnectedCallback() {
    this.disarm();
    super.disconnectedCallback();
  }

  // The dismiss listeners arm when `open` flips true; re-triggering the same
  // toast while it is already visible simply keeps the current one up.
  private arm(): void {
    document.addEventListener('pointerdown', this._onGlobalInteraction, true);
    document.addEventListener('keydown', this._onGlobalInteraction, true);
  }

  private disarm(): void {
    document.removeEventListener('pointerdown', this._onGlobalInteraction, true);
    document.removeEventListener('keydown', this._onGlobalInteraction, true);
  }

  private dismiss(): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.dispatchEvent(new CustomEvent('luna-dismiss', { detail: {}, bubbles: true, composed: true }));
  }

  content() {
    // The container is always in the document so the live region exists
    // before its content changes; the message renders only while open.
    return html`
      <div
        class="luna-toast luna-toast--${this._role} ${this.open ? 'luna-toast--visible' : ''}"
        role="status"
        aria-live="polite"
      >
        ${this.open
          ? html`
              <luna-icon
                name=${this._role === 'success' ? 'check' : 'flag'}
                size="20"
                aria-hidden="true"
              ></luna-icon>
              <span class="luna-toast__message mbw-type-label-chip">${this.message}</span>
            `
          : ''}
      </div>
    `;
  }

  styles() {
    return css`
      .luna-toast {
        position: fixed;
        left: 50%;
        top: 50%;
        /* Above overlays (their scrims sit at z-index 20). */
        z-index: 30;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px; /* pinned: figma Toast */
        box-sizing: border-box;
        height: 100px; /* pinned: figma Toast */
        padding: 14px 22px 14px 18px; /* pinned: figma Toast */
        border-radius: 12px; /* pinned: figma Toast */
        color: #ffffff; /* pinned: figma Toast */
        pointer-events: none; /* never block input (figma component contract) */
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.12),
          0 10px 12px rgba(0, 0, 0, 0.28); /* pinned: figma Toast */
        opacity: 0;
        transform: translate(-50%, calc(-50% + 8px));
        transition:
          opacity var(--mbw-motion-dur-fast),
          transform var(--mbw-motion-dur-fast);
      }

      .luna-toast--visible {
        opacity: 1;
        transform: translate(-50%, -50%);
      }

      .luna-toast--success {
        background: #1f5f2e; /* pinned: figma Toast */
      }

      .luna-toast--info {
        background: var(--luna-edge-blue); /* pinned: figma Toast (#0a3aa0) */
      }

      .luna-toast__message {
        white-space: nowrap; /* pinned: figma Toast */
      }
    `;
  }
}
